import { useState, useCallback, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Deck, Card, Faction } from '../types'
import { useAppStore, deckStats } from '../store'
import { DeckEditor } from '../components/deck/DeckEditor'
import { CardList } from '../components/deck/CardList'
import { DeckStats } from '../components/deck/DeckStats'
import { ManaCurve } from '../components/deck/ManaCurve'
import { ExportModal } from '../components/deck/ExportModal'
import {
  Save,
  Download,
  Trash2,
  ArrowLeft,
  Play,
  Settings,
  RotateCcw,
} from 'lucide-react'

export default function DeckBuilderPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const state = useAppStore.getState()
  const actions = state.actions

  const cards = useAppStore((s) => s.cards)
  const decks = useAppStore((s) => s.decks)
  const user = useAppStore((s) => s.user)
  const deckEditor = useAppStore((s) => s.deckEditor)

  const [currentDeck, setCurrentLocalDeck] = useState<Deck | null>(null)
  const [showExport, setShowExport] = useState(false)
  const [deckName, setDeckName] = useState('Nuevo Mazo')
  const [deckFormat, setDeckFormat] = useState('standard')
  const [activeTab, setActiveTab] = useState<'main' | 'reserve'>('main')

  useEffect(() => {
    if (id && decks.length > 0) {
      const existingDeck = decks.find((d) => d.id === id)
      if (existingDeck) {
        setCurrentLocalDeck(existingDeck)
        actions.setCurrentDeck(existingDeck)
        setDeckName(existingDeck.name)
        setDeckFormat(existingDeck.format)
      }
    } else if (!id && !currentDeck) {
      const newDeck: Deck = {
        id: crypto.randomUUID(),
        name: deckName,
        format: deckFormat,
        isPublic: false,
        userId: user?.user?.id || 'guest',
        cards: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      setCurrentLocalDeck(newDeck)
      actions.setCurrentDeck(newDeck)
    }
  }, [id, decks, user, currentDeck, deckName, deckFormat, actions])

  const handleUpdateDeck = useCallback((updatedDeck: Deck) => {
    setCurrentLocalDeck(updatedDeck)
    actions.setCurrentDeck(updatedDeck)
    actions.setDeckDirty(true)
  }, [actions])

  const handleSave = useCallback(async () => {
    if (!currentDeck) return

    try {
      if (currentDeck.id && decks.some((d) => d.id === currentDeck.id)) {
        actions.updateDeck(currentDeck.id, currentDeck)
      } else {
        const newDeck = { ...currentDeck, id: crypto.randomUUID() }
        actions.addDeck(newDeck)
        actions.setCurrentDeck(newDeck)
        navigate(`/deck/${newDeck.id}`, { replace: true })
      }
      actions.setDeckDirty(false)
    } catch (err) {
      console.error('Error saving deck:', err)
    }
  }, [currentDeck, decks, navigate, actions])

  const handleNewDeck = useCallback(() => {
    if (deckEditor.isDirty) {
      if (!confirm('Tienes cambios sin guardar. Deseas continuar?')) return
    }
    const newDeck: Deck = {
      id: crypto.randomUUID(),
      name: 'Nuevo Mazo',
      format: 'standard',
      isPublic: false,
      userId: user?.user?.id || 'guest',
      cards: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    setCurrentLocalDeck(newDeck)
    actions.setCurrentDeck(newDeck)
    setDeckName('Nuevo Mazo')
    setDeckFormat('standard')
    actions.setDeckDirty(false)
    navigate('/deck/new', { replace: true })
  }, [deckEditor.isDirty, user, navigate, actions])

  const handleCardSelect = useCallback(
    (card: Card) => {
      if (!currentDeck) return

      const formatConfig = {
        maxCards: 60,
        maxCopies: 3,
        maxReserve: 15,
        allowedFactions: Object.values(Faction),
        bannedCards: [] as string[],
      }

      const currentTotal = currentDeck.cards.reduce((sum, c) => sum + c.quantity, 0)

      if (currentTotal >= formatConfig.maxCards) {
        alert('El mazo ya tiene el maximo de cartas permitidas.')
        return
      }

      const existingCard = currentDeck.cards.find((c) => c.cardId === card.id)
      if (existingCard && existingCard.quantity >= formatConfig.maxCopies) {
        alert(`Ya tienes el maximo de ${formatConfig.maxCopies} copias de esta carta.`)
        return
      }

      if (formatConfig.bannedCards.includes(card.id)) {
        alert('Esta carta esta baneada en el formato actual.')
        return
      }

      if (!formatConfig.allowedFactions.includes(card.faction)) {
        alert(`Las cartas de ${card.faction} no estan permitidas en este formato.`)
        return
      }

      actions.addCardToDeck(card.id)
    },
    [currentDeck, actions]
  )

  const handleDelete = useCallback(() => {
    if (!currentDeck || !confirm('Estas seguro de eliminar este mazo?')) return

    if (currentDeck.id) {
      actions.deleteDeck(currentDeck.id)
    }
    actions.clearDeckEditor()
    navigate('/decks')
  }, [currentDeck, navigate, actions])

  const stats = deckStats()
  const formatRules = {
    maxCards: 60,
    maxCopies: 3,
    maxReserve: 15,
    allowedFactions: Object.values(Faction),
  }

  useEffect(() => {
    const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'
    const url = API_URL.endsWith('/api') ? `${API_URL}/cards` : `${API_URL}/api/cards`
    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.data.length > 0) {
          const cards = data.data.map((c: any) => ({
            id: c.id,
            cardId: c.card_id,
            name: c.name,
            cost: c.cost,
            type: c.type,
            faction: c.faction,
            strength: c.strength,
            goldCost: c.gold_cost,
            effect: c.effect ? { description: c.effect } : undefined,
            imageUrl: c.image_url,
            imagePath: c.image_path,
            rarity: c.rarity,
            edition: c.edition,
            race: c.race || c.Raza || c.raza,
          }))
          console.log('Primer carta cargada:', cards[0])
          actions.setCards(cards)
        }
      })
      .catch(console.error)
  }, [actions])

  if (cards.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Cargando cartas...</h1>
          <p className="text-gray-400">
            Primero debes sincronizar las cartas desde la base de datos.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen flex flex-col bg-gray-900">
      <header className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <input
            type="text"
            value={deckName}
            onChange={(e) => {
              setDeckName(e.target.value)
              if (currentDeck) {
                handleUpdateDeck({ ...currentDeck, name: e.target.value })
              }
            }}
            className="bg-transparent text-white font-bold text-xl focus:outline-none focus:border-b-2 focus:border-blue-500 px-2 py-1"
          />
          <select
            value={deckFormat}
            onChange={(e) => {
              setDeckFormat(e.target.value)
              if (currentDeck) {
                handleUpdateDeck({ ...currentDeck, format: e.target.value })
              }
            }}
            className="bg-gray-700 text-gray-300 rounded px-2 py-1 text-sm"
          >
            <option value="standard">Standard</option>
            <option value="extended">Extended</option>
            <option value="all">All Cards</option>
          </select>
          {deckEditor.isDirty && (
            <span className="text-yellow-400 text-sm">Sin guardar</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleNewDeck}
            className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg text-sm"
          >
            <RotateCcw className="w-4 h-4" />
            Nuevo
          </button>
          <button
            onClick={() => setShowExport(true)}
            className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg text-sm"
          >
            <Download className="w-4 h-4" />
            Exportar
          </button>
          <button
            onClick={handleDelete}
            className="flex items-center gap-2 px-3 py-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded-lg text-sm"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <button
            onClick={handleSave}
            disabled={!deckEditor.isDirty}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            Guardar
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        <div className="w-80 flex-shrink-0 border-r border-gray-700">
          <CardList onSelectCard={handleCardSelect} />
        </div>

        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex border-b border-gray-700">
            <button
              onClick={() => setActiveTab('main')}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'main'
                  ? 'text-white border-b-2 border-blue-500'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Mazo Principal ({stats?.totalCards || 0})
            </button>
            <button
              onClick={() => setActiveTab('reserve')}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'reserve'
                  ? 'text-white border-b-2 border-blue-500'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Reserva (0/15)
            </button>
          </div>

          {currentDeck && activeTab === 'main' && (
            <div className="flex-1 overflow-hidden">
              <DeckEditor onUpdate={handleUpdateDeck} />
            </div>
          )}

          {activeTab === 'reserve' && (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <p>La zona de reserva estara disponible pronto</p>
            </div>
          )}
        </div>

        <div className="w-72 flex-shrink-0 border-r border-gray-700 overflow-y-auto">
          <div className="p-4 space-y-4">
            <ManaCurve />
            <DeckStats formatRules={formatRules} />

            {stats && stats.totalCards >= 60 && (
              <button
                onClick={() => navigate(`/battle?deck=${currentDeck?.id}`)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-500"
              >
                <Play className="w-4 h-4" />
                Probar en Arena
              </button>
            )}

            <button
              onClick={() => navigate(`/evolution?deck=${currentDeck?.id}`)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-500"
            >
              <Settings className="w-4 h-4" />
              Evolucionar Mazo
            </button>
          </div>
        </div>
      </div>

      {showExport && currentDeck && (
        <ExportModal deck={currentDeck} onClose={() => setShowExport(false)} />
      )}
    </div>
  )
}
